const DEFAULT_DOB = '05/29/2023';

/** Main Functions */
export function getPageInfo() {
  /*
    Description: This function will get the page info from the note
    Return: A dictionary with the page info
        {
            "URL": Base URL for the page,
            "Note Type": "PT Daily/SOAP Note",...
        }
    */
  const baseURL = window.location.origin;

  // Extract Note Type (first word in the button text)
  const buttonElement = document.querySelector('.t-Region-titleButton');
  let noteType = null;

  if (buttonElement) {
    const parts = buttonElement.textContent.split('|');
    noteType = parts.length > 0 ? parts[0].trim() : null;
  }
  const patientInfo = getPatientInfo();
  if (patientInfo['First Name'] + ' ' + patientInfo['Last Name'] == noteType) {
    noteType = 'Default Note';
  }
  return {
    URL: baseURL,
    'Note Type': noteType
  };
}

export function getPatientInfo() {
  /*
    Description: This function will get the patient info from the note
    Return: A dictionary with the patient info
    {
        "EMR ID": 0x1234 or "michalskij",
        "First Name": firstName,
        "Last Name": lastName,
        "DOB": dob,
    }
    */

  // Extract Patient Name
  const nameElement = document.querySelector("[id$='_PATIENT_KEY_DISPLAY']");
  let firstName = null;
  let lastName = null;

  if (nameElement) {
    const nameText = nameElement.textContent.trim();
    const nameParts = nameText.split(' ');
    if (nameParts.length >= 2) {
      firstName = nameParts[0];
      lastName = nameParts.slice(-1)[0]; // Handles cases with middle names
    } else {
      // alert("Unable to extract patient name correctly.");
    }
  } else {
    // alert("Patient name element not found.");
  }

  // Extract DOB
  const dobElement = document.querySelector("[id$='_DOB_DISPLAY']");
  const dob = dobElement ? dobElement.textContent.trim() : null;

  // Extract EMR ID from the URL
  const emrID = `${firstName}${lastName}`;

  return {
    'EMR ID': emrID,
    'First Name': firstName,
    'Last Name': lastName,
    DOB: dob
  };
}

/* 
questions - This is where we will store the questions
          - a list of dictionaries
     [{
        "Question": "Where was the injury?", 
        "Type": "Multiple Choice", 
        "Choices": ["Back", "Neck", "Shoulder"]
     },
        ...
        ]

*/
var questions = [];

/*
question Interactions - This is where we will store the interactions for each question
                        - a dictionary of dictionaries
         {
            "Where was the injury?": {
                "Type": "Multiple Choice",
                "Choices"{
                    "Back": () => {click the back button},
                    "Neck": () => {click the neck button},
                    "Shoulder": () => {click the shoulder button}
                }
            },
        },
        {
            "What is the injury?": {
                "Type": "Long Answer",
                "Input": (val) => {input the value into the input box}
        }
            ...
         }
*/
var questionInteractions = {};

function findHeadingByClassRecursive(te, targetClass, levelsUp) {
  // Base case: if levelsUp reaches 0 or te is null, stop
  if (levelsUp === 0 || !te) {
    return te?.querySelector(`.${targetClass}`);
  }
  // Recursive call, moving one level up
  return findHeadingByClassRecursive(
    te.parentElement,
    targetClass,
    levelsUp - 1
  );
}

export function getQuestions() {
  questions = [];
  questionInteractions = {};

  // Select all textarea elements on the page
  const textAreas = document.querySelectorAll('textarea');

  // Iterate over each textarea element
  textAreas.forEach((te) => {
    try {
      const labelElement = te
        .closest('.t-Form-fieldContainer')
        ?.querySelector('label');
      let key = labelElement
        ? labelElement.textContent.trim().replace(/:$/, '')
        : null;
      if (!key) return; // Skip if no label is found

      // Section Header too
      const headingElement = findHeadingByClassRecursive(
        te,
        't-Region-header',
        10
      ); // Go up 10 levels
      const headerText = headingElement
        ? headingElement.textContent.trim()
        : 'Note';

      if (
        headerText !== 'Note' &&
        !key.toLowerCase().includes(headerText.toLowerCase())
      ) {
        key = `${headerText} - ${key}`;
      }

      const existingNote = te.value.trim();

      // Set up each entry in questionInteractions
      questionInteractions[key] = {
        Type: 'Long Answer',
        Question: key,
        Key: key,
        ExistingNote: existingNote,
        Input: (val, replyType) => {
          if (isEmptyOrNull(val)) {
            return;
          }
          if (replyType === 'append') {
            te.value = existingNote + '\n' + val;
          } else {
            te.value = val;
          }
        }
      };

      // Also throw it into the questions list
      questions.push({
        Question: key,
        Key: key,
        Type: 'Long Answer',
        ExistingNote: existingNote
      });
    } catch (error) {
      console.log('Error processing textarea:', te);
    }
  });
  return questions;
}

export function answerQuestions(answers) {
  /*
    Description: This function will take the answers and populate the questions
    
    Args: 
        answers: A dictionary of answers
            "Question": "The question in question"
            "Answer": "The answer to the question"
   
    Return: A list of any questions that were not answered, perhaps the answer didn't match the questions expected or whatnot...
        [
            {
                Question: "Where was the injury?", 
                Answer: "Back" 
                Error: "The error message" (Choice not found, etc)
            },
            ...
        }

    */
  var errors = [];
  answers.forEach((answer) => {
    var error = answerQuestion(
      answer['Question'],
      answer['Answer'],
      answer['Reply Type']
    );
    if (error) {
      errors.push({
        Question: answer['Question'],
        Answer: answer['Answer'],
        Error: error
      });
    }
  });
  console.error('Errors:');
  console.error(errors);
  return errors;
}

export function answerQuestion(question, answer, replyType) {
  /*
    Description: This function will take the answer and populate the question
    
    Args: 
        question: The question in question
        answer: The answer to the question
   
    Return: null if success otherwise a string with the error message
    */

  // console.log("Answering Question: '" + question + "' with '" + answer+"'")

  // Make sure the question exists
  if (questionInteractions[question] == null) {
    console.error('Question not found');
    console.error(question);
    // console.log(questionInteractions)
    // console.log(Object.keys(questionInteractions))

    return 'Question not found';
  }

  // If it is a Long Answer or long answer, just populate the input
  if (questionInteractions[question]['Input']) {
    questionInteractions[question]['Input'](answer, replyType);
    return true;
  }

  // Check if the answer is a list of answers or just a single answer
  if (Array.isArray(answer)) {
    // Choose All
    var notFoundAnswers = [];
    for (var i = 0; i < answer.length; i++) {
      if (answer[i] in questionInteractions[question]['Choices']) {
        console.log('Answer found ' + answer[i]);
        questionInteractions[question]['Choices'][answer[i]](true);
        // sleep(1000).then(() => {
        //     questionInteractions[question][answer[i]]()
        // });
      } else {
        notFoundAnswers.push(answer[i]);
      }
    }
    if (notFoundAnswers.length > 0) {
      console.error('The following answers were not found: ' + notFoundAnswers);
      return 'The following answers were not found: ' + notFoundAnswers;
    } else {
      return true;
    }
  } else {
    // Multiple Choice
    if (answer in questionInteractions[question]['Choices']) {
      questionInteractions[question]['Choices'][answer]();
      return true;
    } else {
      console.error('Answer not found');
      console.log('Question: ' + question);
      console.log('Answer:' + answer);
      console.log(questionInteractions[question]['Choices']);
      return 'Answer not found';
    }
  }
}

function isEmptyOrNull(value) {
  return (
    value === null || value === undefined || value === '' || value === 'None'
  );
}

export async function getLogFromInjector() {
  try {
    const htmlContent = document.documentElement.outerHTML;
    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent: htmlContent }
    });
    return { htmlContent: htmlContent };
  } catch (error) {
    // Return the full HTML content of the document
    console.error('Error occurred while fetching document:', error);
    getLogFromInjector();
  }
}
